"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { gsap } from "gsap";
import HomeHero from "@/components/HomeHero";
import VideoRow from "@/components/VideoRow";
import MotionHighlight, { MotionHighlightItem } from "@/components/ui/motion-highlight";
import { Button } from "@/components/ui/button";
import { FaInstagram, FaFacebookF, FaTiktok, FaYoutube } from "react-icons/fa";
import { useReveal } from "@/lib/reveal";
import { HERO_VIDEOS, MIXED_ROW_1, YOUTUBE_BIO } from "@/lib/content";
import BOOKS from "@/app/books/data";
import TextGenerateEffect from "@/components/TextGenerateEffect";

/* TEMP: imported directly (no dynamic/Suspense) to test whether the
   one-time removeChild crash on first navigation is caused by React's
   unmount walking through an ssr:false Suspense boundary. Originally
   these were `dynamic({ ssr: false })` for a smaller initial payload
   — if direct imports fix the crash, we'll restore splitting with a
   safer loader. */
import MaximaTherapyStickyCards from "@/components/MaximaTherapyStickyCards";
import WaabiScrollSection from "@/components/WaabiScrollSection";
import StickyCardsSection from "@/components/StickyCardsSection";
import HighlightSlider from "@/components/HighlightSlider";
import HomeBlogSection from "@/components/HomeBlogSection";

const STATS = [
  { target: 20, suffix: "+", label: "Years" },
  { target: 34, label: "Publications" },
  { target: 151, label: "Presentations" },
  { target: 27, label: "Triathlons" },
  { target: 3, label: "Books" },
];

function BioQuoteSection() {
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-[1300px] px-6 md:px-10">
        <div className="flex min-h-[42svh] items-center justify-center py-6 md:min-h-[48svh] md:py-8">
          <TextGenerateEffect
            words={YOUTUBE_BIO}
            className="mx-auto max-w-[920px] text-center t-xl font-normal"
            style={{ lineHeight: 1.25, letterSpacing: "-0.015em" }}
            duration={0.5}
            staggerDelay={0.03}
            filter={false}
          />
        </div>
      </div>
    </section>
  );
}

function FollowDockSection() {
  const socials = [
    {
      label: "Instagram",
      href: "https://instagram.com/burakozgurmd",
      icon: FaInstagram,
      color: "#E1306C",
    },
    {
      label: "Facebook",
      href: "https://facebook.com/BurakOzgurMD",
      icon: FaFacebookF,
      color: "#1877F2",
    },
    {
      label: "TikTok",
      href: "https://tiktok.com/@burak.ozgur.md",
      icon: FaTiktok,
      color: "#111111",
    },
    {
      label: "YouTube",
      href: "https://www.youtube.com/@burakozgurmd",
      icon: FaYoutube,
      color: "#FF0000",
    },
  ];

  return (
    <section className="bg-white">
      <div className="mx-auto max-w-[1300px] px-6 pb-10 pt-2 md:px-10 md:pb-16">
        <div className="mx-auto max-w-[900px] text-center">
          <TextGenerateEffect
            words="Follow to witness the pursuit of the impossible."
            className="t-xl font-normal"
            duration={0.45}
            staggerDelay={0.025}
            filter={false}
            style={{ lineHeight: 1.25, letterSpacing: "-0.015em" }}
          />

          <MotionHighlight
            className="bg-zinc-100/80"
            activeClassName="bg-zinc-100 text-black rounded-xl border border-black/10 shadow-[0_8px_24px_rgba(0,0,0,0.08)]"
            hover
            controlledItems
            transition={{ type: "spring", stiffness: 280, damping: 24 }}
          >
            <div className="mx-auto mt-8 inline-flex flex-wrap items-center justify-center gap-2 rounded-2xl border border-black/10 p-2 md:flex-nowrap">
              {socials.map((item) => (
                <MotionHighlightItem key={item.label} asChild>
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-11 min-w-[132px] items-center justify-center gap-2 rounded-xl px-4 text-sm font-normal text-black/85 transition-colors"
                  >
                    <item.icon className="h-4 w-4" style={{ color: item.color }} />
                    {item.label}
                  </a>
                </MotionHighlightItem>
              ))}
              <MotionHighlightItem asChild>
                <Link
                  href="/contact"
                  className="inline-flex h-11 min-w-[132px] items-center justify-center rounded-xl border border-black/15 bg-white px-4 text-sm font-normal text-black"
                >
                  Book Burak
                </Link>
              </MotionHighlightItem>
            </div>
          </MotionHighlight>
        </div>
      </div>
    </section>
  );
}

function HeadingReveal({ label, title, cta, href }) {
  const ref = useReveal((el) => {
    const words = el.querySelectorAll(".word");
    return gsap.fromTo(
      words,
      { y: 60, opacity: 0 },
      { y: 0, opacity: 1, duration: 1, stagger: 0.04, ease: "power3.out" }
    );
  });

  return (
    <div className="mb-10 flex items-end justify-between gap-6 md:mb-14">
      <div ref={ref} className="reveal-split">
        <div className="mono-label mb-4 opacity-50">{label}</div>
        <h2
          className="t-2xl uppercase"
          style={{ letterSpacing: "-0.035em", fontWeight: 400 }}
        >
          {title.split(" ").map((w, i) => (
            <span key={i} className="word" style={{ display: "inline-block", marginRight: "0.22em" }}>
              {w}
            </span>
          ))}
        </h2>
      </div>
      {cta && href && (
        <Link href={href} className="mono-label shrink-0 opacity-70 hover:opacity-100">
          {cta} →
        </Link>
      )}
    </div>
  );
}

function FeaturedVideos() {
  const [videos, setVideos] = useState(MIXED_ROW_1);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/youtube")
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        if (d?.videos?.length) {
          // keep first 6 regular videos
          setVideos(d.videos.slice(0, 6));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="bg-white">
      <div className="mx-auto max-w-[1600px] px-6 py-24 md:px-10 md:py-28">
        <HeadingReveal label="Watch" title="Featured" />
        <VideoRow videos={videos} />
      </div>
    </section>
  );
}

function FeaturedBooks() {
  return (
    <section className="bg-[#0a0a0a] text-white">
      <div className="mx-auto max-w-[1600px] px-6 py-24 md:px-10 md:py-28">
        <div className="mb-14 flex items-end justify-between gap-6">
          <div>
            <div className="mono-label mb-4 opacity-50">Library</div>
            <h2
              className="t-2xl uppercase"
              style={{ letterSpacing: "-0.035em", fontWeight: 400 }}
            >
              Books by Burak Ozgur
            </h2>
          </div>
          <Link href="/books" className="mono-label shrink-0 opacity-70 hover:opacity-100">
            All books →
          </Link>
        </div>

        <div className="grid gap-10 md:grid-cols-3">
          {BOOKS.map((b) => (
            <Link
              key={b.slug}
              href={`/books/${b.slug}`}
              className="group block"
            >
              <div className="relative aspect-[2/3] overflow-hidden rounded-xl bg-white/5">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={b.image}
                  alt={b.title}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
              </div>
              <div className="mt-5">
                <div className="text-lg">{b.title}</div>
                <div className="mt-1 text-sm opacity-60">{b.subtitle}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  const ref = useReveal((el) => {
    const words = el.querySelectorAll(".word");
    return gsap.fromTo(
      words,
      { y: 40, opacity: 0 },
      { y: 0, opacity: 1, duration: 1, stagger: 0.05, ease: "power3.out" }
    );
  });

  const heading = "Available for media, speaking & collaboration";

  return (
    <section className="bg-white">
      <div className="mx-auto max-w-[1300px] px-6 py-28 text-center md:px-10 md:py-40">
        <div ref={ref} className="reveal-split">
          <h2
            className="t-2xl mx-auto max-w-[900px]"
            style={{ letterSpacing: "-0.025em", lineHeight: 1.1 }}
          >
            {heading.split(" ").map((w, i) => (
              <span
                key={i}
                className="word"
                style={{ display: "inline-block", marginRight: "0.22em" }}
              >
                {w}
              </span>
            ))}
          </h2>
        </div>
        <p className="mx-auto mt-6 max-w-[600px] text-base opacity-70">
          Dr. Ozgur is a compelling on-camera expert and keynote speaker.
        </p>
        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button asChild size="xl">
            <Link href="/contact">Contact</Link>
          </Button>
          <Button asChild size="xl" variant="outline">
            <Link href="/books">Read</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <>
      <HomeHero videos={HERO_VIDEOS} />
      <MaximaTherapyStickyCards />
      <WaabiScrollSection />
      <StickyCardsSection />
      <HighlightSlider />
      <HomeBlogSection />
    </>
  );
}
